import { supabase } from '@sixam/supabase/client';
import { authedFetch } from '@/lib/authed-fetch';
import {
  attendanceStatusFor,
  type EventAttendanceSummary,
  type EventAttendee,
} from '@sixam/shared/event-attendance-core';

export {
  attendanceStatusFor,
  hasAttendanceRecord,
  isAttendanceActive,
  isRegistrationGoing,
  partitionAttendeesByCheckIn,
  type EventAttendanceStatus,
  type EventAttendanceSummary,
  type EventAttendee,
} from '@sixam/shared/event-attendance-core';

export async function fetchMyEventAttendance(
  eventIds: string[]
): Promise<Map<string, EventAttendanceSummary>> {
  if (eventIds.length === 0) {
    return new Map();
  }

  const [registrationsResult, rsvpsResult] = await Promise.all([
    supabase.from('event_registrations').select('*').in('event_id', eventIds),
    supabase.from('event_rsvps').select('*').in('event_id', eventIds),
  ]);

  if (registrationsResult.error) {
    throw registrationsResult.error;
  }
  if (rsvpsResult.error) {
    throw rsvpsResult.error;
  }

  const registrationsByEvent = new Map(
    (registrationsResult.data ?? []).map((registration) => [registration.event_id, registration])
  );
  const rsvpsByEvent = new Map((rsvpsResult.data ?? []).map((rsvp) => [rsvp.event_id, rsvp]));

  return new Map(
    eventIds.map((eventId) => {
      const registration = registrationsByEvent.get(eventId) ?? null;
      const rsvp = rsvpsByEvent.get(eventId) ?? null;
      return [
        eventId,
        {
          eventId,
          registration,
          rsvp,
          status: attendanceStatusFor(registration, rsvp),
        },
      ];
    })
  );
}

export async function fetchMyActiveEventIds(): Promise<string[]> {
  const [registrations, rsvps] = await Promise.all([
    supabase.from('event_registrations').select('event_id').eq('status', 'confirmed'),
    supabase.from('event_rsvps').select('event_id').eq('status', 'going'),
  ]);
  if (registrations.error) throw registrations.error;
  if (rsvps.error) throw rsvps.error;
  return [
    ...new Set([
      ...(registrations.data ?? []).map((row) => row.event_id),
      ...(rsvps.data ?? []).map((row) => row.event_id),
    ]),
  ];
}

export async function fetchEventAttendees(eventId: string): Promise<EventAttendee[]> {
  const response = await authedFetch(`/api/events/${eventId}/attendees`);
  const payload = (await response.json().catch(() => null)) as {
    attendees?: EventAttendee[];
    error?: string;
  } | null;

  if (!response.ok) {
    throw new Error(payload?.error ?? 'Could not load event attendees.');
  }

  return payload?.attendees ?? [];
}
